import { action, makeAutoObservable, runInAction } from "mobx"
import { enterCode, getCurrent, signIn, signUp } from "../../services/auth";
import { removeTokenPair, saveTokenPair } from "../../apiCommandsExecutor/token";
import { SimplifiedAccount } from "../../services/auth/models";
import { ExecutionStatus } from "../../services/general/requestsResponses";
import rootStore, { stores } from "../../stores";
import { OrganizationBranchSimplifiedPublic } from "../../services/organizationBranches/models";
import moment from 'moment';


class AuthStore {
    isAuthorized: boolean;
    currentUser?: SimplifiedAccount;
    isError: boolean = false;
    isLoading: boolean = false;
    showTariffPopUp: boolean = true;
    organizationBranch:  OrganizationBranchSimplifiedPublic | null;

    constructor() {
        makeAutoObservable(this)
    }

    @action
    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    @action
    setShowTariffPopUp(value: boolean) {
        this.showTariffPopUp = value
    }


    @action
    loginAsUser(token: string) {
        saveTokenPair({
            authToken: token,
            authTokenExpiresAt: new Date(),
            refreshToken: "",
            refreshTokenExpiresAt: new Date()
        });
    }

    isTariffOverdue() {
        if (!this.organizationBranch) return false

        const tariffDetails = this.organizationBranch.activeTariffDetails

        const now = moment()
        return now <= moment(tariffDetails.menusActiveTillDate) && now > moment(tariffDetails.nextPaymentDate)
    }

    isPaymentSoon() {
        if (!this.organizationBranch) return false

        const now = moment()
        const nextPaymentDate = moment(this.organizationBranch.activeTariffDetails.nextPaymentDate)

        return nextPaymentDate.diff(now, 'days') <= 7
    }

    menusAliveStr() {
        if (!this.organizationBranch) return ''

        const now = moment()
        const tariffDetails = this.organizationBranch.activeTariffDetails
        let targetDate

        if (this.isTariffAlive()) {
            const nextPaymentDate = moment(tariffDetails.nextPaymentDate)

            if (nextPaymentDate.diff(now, 'days') < 7) {
                targetDate = nextPaymentDate
            } else {
                return ''
            }
        } else {
            if (this.isTariffOverdue()) {
                targetDate = moment(tariffDetails.menusActiveTillDate)
            }
        }

        if (!targetDate) {
            return ''
        }

        const diffDays = targetDate.diff(now, 'days')

        if (diffDays < 1) {
            const diffHours = targetDate.diff(now, 'hours')
            return `${diffHours}ч`
        } else {
            return `${diffDays}д`
        }

    }

    isTariffAlive() {
        if (!this.organizationBranch) return false

        const tariffDetails = this.organizationBranch.activeTariffDetails

        const now = moment()

        return now <= moment(tariffDetails.nextPaymentDate)
    }

    isTariffActive() {
        if (!this.organizationBranch) return false

        const tariffDetails = this.organizationBranch.activeTariffDetails

        const now = moment()

        return now <= moment(tariffDetails.menusActiveTillDate)
    }

    isSimpleFeaturesActive() {
        return this.isTariffActive()
    }

    isBasicFeaturesActive() {
        return this.isTariffActive() && ['Basic', 'Advanced'].includes(this.organizationBranch?.activeTariffDetails.type)
    }

    isAdvancedFeaturesActive() {
        return this.isTariffActive() && this.organizationBranch?.activeTariffDetails.type == 'Advanced'
    }


    async getCurrentUser() {
        this.setIsLoading(true);
        const response = await getCurrent();
        if (response.status === 200) {
            runInAction(() => {
                this.isAuthorized = true;
                this.currentUser = {
                    ...response.data.account,
                };
                this.organizationBranch = {
                    ...response.data.organizationBranch
                }
            });
            stores.constantsStore.init();
        }else{
            await this.logout()
        }
        this.setIsLoading(false);
    }

    async requestAuthCode(phone: string){
        this.setIsLoading(true);
        const response = await signIn(phone);
        if(!response){
            runInAction(()=> {
                this.isAuthorized = false;
            });
            return;
        }
        this.setIsLoading(false);
        return response.executionStatus === ExecutionStatus.Finished;
    }

    async enterAuthCode(params: {phoneNumber: string, authCode: string}){
        this.setIsLoading(true);
        const response = await enterCode(params);
        if(!response){
            runInAction(()=> {
                this.isError = true;
            });
            return;
        }
        saveTokenPair(response.tokenPair);
        await this.getCurrentUser();
        this.setIsLoading(false);
        return response.executionStatus === ExecutionStatus.Finished;
    }

    async signUp(phone: string){
        this.setIsLoading(true);
        const response = await signUp(phone);
        if(!response){
            runInAction(()=> {
                this.isAuthorized = false;
            });
            return;
        }
        saveTokenPair(response.authTokenPair);
        await this.getCurrentUser();
        this.setIsLoading(false);
        return response.executionStatus === ExecutionStatus.Finished;
    }

    async logout(){
        removeTokenPair();
        runInAction(() => {
            this.isAuthorized = false;
            this.currentUser = undefined;
            this.isError = false;
            rootStore.menuStore.reset();
        })
    }
}

export default AuthStore
